import React, { useCallback, useEffect, useMemo } from 'react';

import { RobotBlocklyLoadable } from '../__blockly/robot__blockly-loadable';
import { RobotCodeLightV2Loadable } from '../__code-light-v2/robot__code-light-loadable';
import { RobotCodeLightLoadable } from '../__code-light/robot__code-light-loadable';
import { RobotCodeLoadable } from '../__code/robot__code-loadable';
import { useRobotSelector, useEditor } from '../context';
import { type BlocklyPythonAnswer, type BlocklyPythonEditor, EDITOR_TYPES } from '../robot.types';
import { getIsReadOnly } from '../selectors';
import { convertToObjectAnswer } from '../utils/convertToObjectAnswer';
import { getCodeFromUserAnswer } from '../utils/getCodeFromUserAnswer';

import { useErrorLineNumber, useReadOnly, useUncontrolledCodeInput } from './hooks';

export const Code = () => {
    const readOnly = useReadOnly();

    const [handleChange, defaultAnswer, key] = useUncontrolledCodeInput();

    const defaultCode = useMemo(() => {
        return getCodeFromUserAnswer(defaultAnswer);
    }, [defaultAnswer]);

    const onChange = useCallback(
        (_1: unknown, _2: unknown, value: string) => {
            handleChange(convertToObjectAnswer(value));
        },
        [handleChange]
    );

    return (
        <RobotCodeLoadable
            errorLineNumber={useErrorLineNumber()}
            functionHints={useRobotSelector((s) => s.problem.markup.options.function_hints)}
            initialValue={defaultCode}
            key={key}
            lineNumber={useRobotSelector((s) => s.state.lineNumber)}
            onChange={onChange}
            options={useMemo(() => ({ readOnly }), [readOnly])}
        />
    );
};

export const Light = () => {
    const readOnly = useRobotSelector(getIsReadOnly);
    const [handleChange, defaultAnswer, key] = useUncontrolledCodeInput();

    const defaultCode = useMemo(() => {
        return getCodeFromUserAnswer(defaultAnswer);
    }, [defaultAnswer]);

    const onChange = useCallback(
        (value: string) => {
            handleChange(convertToObjectAnswer(value, EDITOR_TYPES.LIGHT_PYTHON));
        },
        [handleChange]
    );

    return (
        <RobotCodeLightLoadable
            defaultValue={defaultCode}
            key={key}
            lineNumber={useRobotSelector((s) => s.state.lineNumber)}
            onChange={onChange}
            readOnly={readOnly}
        />
    );
};

export const LightV2 = () => {
    const readOnly = useRobotSelector(getIsReadOnly);
    const [handleChange, defaultAnswer, key] = useUncontrolledCodeInput();

    const defaultCode = useMemo(() => {
        return getCodeFromUserAnswer(defaultAnswer);
    }, [defaultAnswer]);

    const onChange = useCallback(
        (value: string) => {
            handleChange(convertToObjectAnswer(value, EDITOR_TYPES.CODE_LIGHT_V2));
        },
        [handleChange]
    );

    return (
        <RobotCodeLightV2Loadable
            defaultValue={defaultCode}
            key={key}
            lineNumber={useRobotSelector((s) => s.state.lineNumber)}
            onChange={onChange}
            readOnly={readOnly}
        />
    );
};

interface BlocklyProps {
    className?: string;
}

export const Blockly = ({ className }: BlocklyProps) => {
    const readOnly = useRobotSelector(getIsReadOnly);
    const [handleChange, defaultValue, key] = useUncontrolledCodeInput();

    const onChange = useCallback(
        (answer: Omit<BlocklyPythonAnswer, 'editor'>) => {
            handleChange({
                version: '1' as const,
                answer: { ...answer, editor: EDITOR_TYPES.BLOCKLY_PYTHON },
            });
        },
        [handleChange]
    );

    const functionHints = useRobotSelector((s) => s.problem.markup.options.function_hints);
    const editor = useEditor() as BlocklyPythonEditor;
    const availableBlocks = editor.available_blocks;

    return (
        <RobotBlocklyLoadable
            availableBlocks={availableBlocks}
            className={className}
            defaultValue={defaultValue?.answer as BlocklyPythonAnswer | undefined}
            functionHints={functionHints}
            key={key}
            lineNumber={useRobotSelector((s) => s.state.lineNumber)}
            onChange={onChange}
            readOnly={readOnly}
        />
    );
};

//а надо ли? EDUCATION-31217
export const PreloadCode = () => {
    const { editor } = useEditor();

    useEffect(() => {
        if (editor === EDITOR_TYPES.PYTHON) {
            RobotCodeLoadable.preload();
        } else if (editor === EDITOR_TYPES.LIGHT_PYTHON) {
            RobotCodeLightLoadable.preload();
        } else if (editor === EDITOR_TYPES.BLOCKLY_PYTHON) {
            RobotBlocklyLoadable.preload();
        } else if (editor === EDITOR_TYPES.CODE_LIGHT_V2) {
            RobotCodeLightV2Loadable.preload();
        }
    }, [editor]);

    return null;
};
